/**
 * Utility functions for handling period strings (quarters, weeks, and years)
 */

interface ParsedPeriod {
  year: number;
  type: 'Q' | 'W';
  num: number;
  original: string;
}

export interface ParsedYearPeriod {
  year: number;
  type: 'Y';
  original: string;
}

/**
 * Parse a period string like "2025Q4" or "2025W47"
 * @param period - Period string in format YYYYQN or YYYYWNN
 * @returns Parsed period object or null if invalid
 */
export function parsePeriod(period: string): ParsedPeriod | null {
  const match = period.match(/^(\d{4})(Q|W)(\d+)$/);
  if (!match) return null;

  const [, yearStr, type, numStr] = match;
  return {
    year: parseInt(yearStr, 10),
    type: type as 'Q' | 'W',
    num: parseInt(numStr, 10),
    original: period
  };
}

/**
 * Parse a year period string like "2025Y"
 * @param period - Period string in format YYYYY
 * @returns Parsed year period object or null if invalid
 */
export function parseYearPeriod(period: string): ParsedYearPeriod | null {
  const match = period.match(/^(\d{4})Y$/);
  if (!match) return null;

  return {
    year: parseInt(match[1], 10),
    type: 'Y',
    original: period
  };
}

/**
 * Check if period is a year filter
 * @param period - Period string to check
 * @returns True if period is a year filter (e.g., "2025Y")
 */
export function isYearPeriod(period: string): boolean {
  return period.endsWith('Y');
}

/**
 * Check if period is a daily filter (YYYYDNNN format, e.g. "2026D139")
 */
export function isDayPeriod(period: string): boolean {
  return /^\d{4}D\d{1,3}$/.test(period);
}

export interface ParsedDayPeriod {
  year: number;
  dayOfYear: number;
  type: 'D';
  original: string;
}

/**
 * Parse a daily period string like "2026D139"
 */
export function parseDayPeriod(period: string): ParsedDayPeriod | null {
  const match = period.match(/^(\d{4})D(\d{1,3})$/);
  if (!match) return null;
  return {
    year: parseInt(match[1], 10),
    dayOfYear: parseInt(match[2], 10),
    type: 'D',
    original: period
  };
}

/**
 * Get quarters for a year (e.g., 2025 -> ['2025Q1', '2025Q2', '2025Q3', '2025Q4'])
 * @param year - Year number
 * @returns Array of quarter strings for the year
 */
export function getQuartersForYear(year: number): string[] {
  return [
    `${year}Q1`,
    `${year}Q2`,
    `${year}Q3`,
    `${year}Q4`
  ];
}

/**
 * Derive available years from quarters (e.g., ['2025Q1', '2024Q1'] -> ['2025Y', '2024Y'])
 * Only include years that have Q1 data (since year filter = Q1-to-Q1 comparison)
 * @param quarters - Array of quarter period strings
 * @returns Array of year period strings sorted descending
 */
export function deriveYearsFromQuarters(quarters: string[]): string[] {
  const years = new Set<number>();

  quarters.forEach(quarter => {
    // Only include years that have Q1 data
    if (quarter.endsWith('Q1')) {
      const parsed = parsePeriod(quarter);
      if (parsed && parsed.type === 'Q') {
        years.add(parsed.year);
      }
    }
  });

  // Sort descending (newest first) and format as "YYYYY"
  return Array.from(years)
    .sort((a, b) => b - a)
    .map(year => `${year}Y`);
}

/**
 * Sort periods with weeks first (descending), then quarters (descending), then years (descending)
 * @param periods - Array of period strings
 * @returns Sorted array of period strings
 */
export function sortPeriods(periods: string[]): string[] {
  const parsed = periods
    .map(p => {
      if (isDayPeriod(p)) {
        const day = parseDayPeriod(p);
        return day ? { ...day, num: day.dayOfYear } : null;
      }
      if (isYearPeriod(p)) {
        const year = parseYearPeriod(p);
        return year ? { ...year, num: 0 } : null;
      }
      return parsePeriod(p);
    })
    .filter((p): p is (ParsedPeriod | (ParsedYearPeriod & { num: number }) | (ParsedDayPeriod & { num: number })) => p !== null);

  // Separate by type
  const days = parsed.filter(p => p.type === 'D');
  const quarters = parsed.filter(p => p.type === 'Q');
  const weeks = parsed.filter(p => p.type === 'W');
  const years = parsed.filter(p => p.type === 'Y');

  // Sort each group: newest first (descending by year, then by number)
  const sortDesc = (a: any, b: any) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.num - a.num;
  };

  days.sort(sortDesc);
  quarters.sort(sortDesc);
  weeks.sort(sortDesc);
  years.sort(sortDesc);

  // Daily first (most granular/recent), then weeks, quarters, years
  return [...days, ...weeks, ...quarters, ...years].map(p => p.original);
}
